import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Users, Search, X, FlaskConical, ChevronRight, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * StaffPanelPickerDialog — opens from the navbar's "Panels → Staff Panel".
 *
 * Two modes, both mint a STAFF-shaped JWT via `/api/staff/admin-bypass-login`
 * and stash it in sessionStorage (where StaffDashboardPage expects it):
 *
 *   1. "Open as myself (testing)" → admin gets a synthetic supervisor
 *      staff token (sentinel staff_id="ADMIN-BYPASS-<mobile>"). Useful
 *      for a UI sanity check of the staff dashboard.
 *   2. "Open as <staff>"          → mints the real staff member's token
 *      so the admin sees exactly what that staff sees on /staff.
 *
 * Admin's regular customer/admin token in `secureStorage` is UNCHANGED —
 * exiting is just navigating away from /staff. The bypass session lives
 * purely in sessionStorage and dies with the tab.
 */
export default function StaffPanelPickerDialog({ open, onClose }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState('');
  const [myIdentity, setMyIdentity] = useState(null); // /me-by-phone result
  const debRef = useRef(null);

  // 2026-06-19: detect whether the caller has their own staff identity
  // (real np_staff row OR synthesized admin supervisor) so we can show
  // a pinned "Continue as yourself" row. Single call on dialog open.
  useEffect(() => {
    if (!open) {
      setMyIdentity(null);
      return;
    }
    axios
      .get(`${API}/staff/me-by-phone`)
      .then((r) => setMyIdentity(r.data || null))
      .catch(() => setMyIdentity(null));
  }, [open]);

  // Fetch roster on open + on debounced search.
  useEffect(() => {
    if (!open) return;
    clearTimeout(debRef.current);
    debRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/admin/staff-roster`, {
          params: { q: query, limit: 100 },
        });
        setRoster(r.data?.staff || []);
      } catch (e) {
        setRoster([]);
        toast.error('Could not load staff roster');
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(debRef.current);
  }, [open, query]);

  if (!open) return null;

  // Common: mint staff bypass token, stash it, land on /staff.
  const mintAndLand = async (staffId /* null = self-test */, labelForBusy = 'self') => {
    setBusyId(labelForBusy);
    try {
      const body = staffId ? { staff_id: staffId } : {};
      const r = await axios.post(`${API}/staff/admin-bypass-login`, body);
      const { token, staff } = r.data || {};
      if (!token || !staff) throw new Error('Bad bypass response');
      sessionStorage.setItem('staff_token', token);
      sessionStorage.setItem('staff_user', JSON.stringify(staff));
      onClose?.();
      // Hard navigation so any side-effects of the staff dashboard re-init cleanly.
      window.location.href = '/staff';
    } catch (err) {
      const msg = err.response?.data?.detail || 'Could not open staff panel';
      toast.error(msg);
      setBusyId('');
    }
  };

  const handleAsAdmin = () => mintAndLand(null, 'self');
  const handleAsStaff = (s) => mintAndLand(s.staff_id, s.staff_id);

  // Role badge palette.
  const roleColor = (role) => {
    const r = (role || '').toLowerCase();
    if (r === 'supervisor')        return 'bg-purple-100 text-purple-800';
    if (r === 'floor_manager')     return 'bg-amber-100 text-amber-800';
    if (r === 'qc_inspector')      return 'bg-blue-100 text-blue-800';
    if (r === 'packer')            return 'bg-emerald-100 text-emerald-800';
    if (r === 'stock_keeper')      return 'bg-cyan-100 text-cyan-800';
    if (r === 'dispatch_handler')  return 'bg-rose-100 text-rose-800';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/50 flex items-start justify-center p-4 pt-12 md:pt-20"
      onClick={onClose}
      data-testid="staff-panel-picker-backdrop"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[82vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="staff-panel-picker-modal"
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-200 flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-100 text-indigo-700 flex items-center justify-center shrink-0">
            <Users className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-base font-bold text-gray-900">Staff Panel</div>
            <div className="text-xs text-gray-500 leading-snug">
              Test the staff dashboard yourself, or open it as a specific
              staff member to see their exact view.
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 shrink-0"
            data-testid="staff-panel-picker-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* 2026-06-19: Continue-as-yourself pinned row.
            Shown when /me-by-phone reports an identity for the caller:
              • `np_staff` source — their real warehouse staff row
              • `admin_auto` — synthesized supervisor for admins/sub-admins
                who aren't registered as np_staff (Roshan/Bhavik in prod).
            Mints the appropriate token + lands on /staff. */}
        {myIdentity?.identity && (
          <button
            onClick={() => {
              const id = myIdentity.identity;
              if (myIdentity.source === 'np_staff') {
                mintAndLand(id.staff_id, id.staff_id);
              } else {
                // admin_auto / staff_token paths use the no-staff_id bypass
                // (synthetic supervisor session) — keeps the existing
                // behaviour and audit trail clean.
                mintAndLand(null, 'self_auto');
              }
            }}
            disabled={!!busyId}
            className="mx-5 mt-4 mb-2 flex items-center gap-3 rounded-xl border-2 border-emerald-300 bg-emerald-50 hover:bg-emerald-100 px-4 py-3 text-left transition-colors disabled:opacity-50"
            data-testid="staff-panel-continue-as-self"
          >
            <div className="w-9 h-9 rounded-lg bg-emerald-200 text-emerald-800 flex items-center justify-center shrink-0 font-bold text-sm">
              {(myIdentity.identity.name || '?').charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-emerald-900 truncate">
                Continue as {myIdentity.identity.name}
              </div>
              <div className="text-[11px] text-emerald-800/80">
                <span className={`inline-block px-1.5 py-0.5 rounded ${roleColor(myIdentity.identity.role)} mr-1`}>
                  {myIdentity.identity.role}
                </span>
                {(myIdentity.identity.location || '').replace('_', ' ')}
                {myIdentity.source === 'admin_auto' && (
                  <span className="ml-2 text-[10px] italic text-emerald-600">auto-granted via admin role</span>
                )}
              </div>
            </div>
            {busyId === 'self_auto' || busyId === myIdentity.identity?.staff_id
              ? <Loader2 className="w-4 h-4 text-emerald-600 animate-spin" />
              : <ChevronRight className="w-4 h-4 text-emerald-500" />}
          </button>
        )}

        {/* Testing-mode shortcut */}
        <button
          onClick={handleAsAdmin}
          disabled={!!busyId}
          className="mx-5 mt-4 mb-2 flex items-center gap-3 rounded-xl border border-dashed border-indigo-300 bg-indigo-50/40 hover:bg-indigo-50 px-4 py-3 text-left transition-colors disabled:opacity-50"
          data-testid="staff-panel-open-as-admin"
        >
          <div className="w-9 h-9 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center shrink-0">
            <FlaskConical className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-indigo-900">
              Open as myself (testing)
            </div>
            <div className="text-[11px] text-indigo-700/80">
              Lands on /staff with a virtual supervisor session.
            </div>
          </div>
          {busyId === 'self'
            ? <Loader2 className="w-4 h-4 text-indigo-500 animate-spin" />
            : <ChevronRight className="w-4 h-4 text-indigo-400" />}
        </button>

        {/* Divider */}
        <div className="px-5 py-1 flex items-center gap-2">
          <div className="h-px flex-1 bg-gray-200" />
          <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">
            or open as a staff member
          </span>
          <div className="h-px flex-1 bg-gray-200" />
        </div>

        {/* Search */}
        <div className="px-5 pt-2 pb-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name / phone / role…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
              data-testid="staff-panel-picker-search"
            />
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-2 pb-3" data-testid="staff-panel-picker-list">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-gray-400 text-sm gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading staff…
            </div>
          ) : roster.length === 0 ? (
            <div className="text-center py-10 text-sm text-gray-500">
              No staff matched your search.
              <div className="text-[11px] text-gray-400 mt-1">
                (Roster is empty? Add staff under <span className="font-mono">Admin → Staff</span>.)
              </div>
            </div>
          ) : (
            roster.map((s) => (
              <button
                key={s.staff_id}
                onClick={() => handleAsStaff(s)}
                disabled={!!busyId}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors text-left disabled:opacity-40"
                data-testid={`staff-panel-pick-${s.staff_id}`}
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 text-white text-xs font-bold flex items-center justify-center shrink-0">
                  {(s.name || s.display_phone || '?').charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-semibold text-gray-900 truncate">
                      {s.name || 'Unnamed'}
                    </div>
                    {s.display_role && (
                      <span className={`text-[10px] uppercase font-semibold tracking-wider px-1.5 py-0.5 rounded ${roleColor(s.display_role)}`}>
                        {s.display_role.replace(/_/g, ' ')}
                      </span>
                    )}
                    {s.display_location && (
                      <span className="text-[10px] text-gray-500">
                        {s.display_location}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-gray-500 truncate font-mono">
                    {s.display_phone || s.staff_id}
                  </div>
                </div>
                {busyId === s.staff_id ? (
                  <Loader2 className="w-4 h-4 text-indigo-500 animate-spin shrink-0" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
                )}
              </button>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex items-center gap-2 text-[11px] text-gray-500">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
          Audit log records every staff-panel bypass. Sign out from inside /staff to exit.
        </div>
      </div>
    </div>
  );
}
